import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  RefreshCw,
  ExternalLink,
  Share2,
  Trash2,
  Loader2,
  Clock,
  Lock,
  Users,
  UserCheck,
  GitBranch,
  Calendar,
  Activity,
  Copy,
  Check,
  Server,
  Cpu,
  HardDrive,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  X,
  Rocket,
  Globe,
  Pencil,
  Key,
  Bell,
  Link2,
  Info,
  LayoutTemplate,
} from 'lucide-react'
import FileBrowserPanel from '../components/workspace/FileBrowserPanel'
import SourceReleasePanel from '../components/source-release/SourceReleasePanel'
import EditAppInfoModal from '../components/EditAppInfoModal'
import ShareModalComponent from '../components/ShareModal'
import CloneAppModal from '../components/CloneAppModal'
import { Switch } from '../components/ui/Switch'
import { StyledSelect } from '../components/ui/StyledSelect'
import {
  getApp as fetchApp,
  startApp,
  stopApp,
  rebuildApp,
  deleteApp,
  updateApp,
  listAppShares,
  requestAccess,
  getAppResources,
  resetAppResources,
  getAppLogs,
  getDeploymentStatus,
  triggerProductionBuild,
  stopProductionBuild,
  getProductionLogs,
  preflightMakeAppLive,
  listAppSecrets,
  upsertAppSecret,
  deleteAppSecret,
  listAccessRequests,
  approveAccessRequest,
  denyAccessRequest,
  type App,
  type AppShare,
  type AccessRequest,
  type AppSecretMetadata,
  type RequiredAppSecret,
  type MergedAppResources,
  type DeploymentStatus,
  type SourcePreflightResponse,
} from '../lib/api'
import { getCloneNotice, type CloneNotice } from '../lib/cloneFeedback'
import { useUser } from '../hooks/useUser'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { HelpLink } from '../components/HelpLink'
import AppUsageStats from '../components/analytics/AppUsageStats'
import VisitorTrendChart from '../components/analytics/VisitorTrendChart'
import InlineCategoryPicker from '../components/InlineCategoryPicker'
import AppActions from '../components/AppActions'
import { useFolders } from '../components/folders/useFolders'
import { withMountBase } from '../lib/mountBase'

const statusConfig: Record<string, { color: string; textColor: string; label: string; pulse: boolean }> = {
  running: { color: 'bg-green-500', textColor: 'text-green-500', label: 'Running', pulse: false },
  stopped: { color: 'bg-gray-500', textColor: 'text-gray-400', label: 'Stopped', pulse: false },
  queued: { color: 'bg-blue-500', textColor: 'text-blue-500', label: 'Queued', pulse: true },
  building: { color: 'bg-blue-500', textColor: 'text-blue-500', label: 'Building...', pulse: true },
  installing: { color: 'bg-amber-500', textColor: 'text-amber-500', label: 'Installing Dependencies...', pulse: true },
  starting: { color: 'bg-amber-500', textColor: 'text-amber-500', label: 'Starting...', pulse: true },
  error: { color: 'bg-red-500', textColor: 'text-red-500', label: 'Error', pulse: false },
}

const deploymentSteps = ['queued', 'building', 'installing', 'starting', 'running'] as const

const statusDescriptions: Record<string, string> = {
  queued: 'Your app is queued for deployment',
  building: 'Setting up the container environment',
  installing: 'Installing dependencies (npm install / pip install)',
  starting: 'Starting your app process',
  running: 'Your app is live and accessible',
  stopped: 'App is not running',
}

const accessConfig = {
  personal: { icon: Lock, label: 'Private', description: 'Only you can access' },
  specific: { icon: UserCheck, label: 'Specific people', description: 'Only people you add' },
  link: { icon: Link2, label: 'Anyone with the link', description: 'Any platform user with the link' },
  platform: { icon: Users, label: 'Platform', description: 'All platform users can access' },
}

const runtimeConfig = {
  node: { label: 'Node.js', color: 'bg-green-500' },
  python: { label: 'Python', color: 'bg-blue-500' },
  static: { label: 'Static', color: 'bg-purple-500' },
  auto: { label: 'Auto', color: 'bg-gray-500' },
}

function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export default function AppDetail() {
  const { appName } = useParams<{ appName: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useUser()


  const [app, setApp] = useState<App | null>(null)
  const [shares, setShares] = useState<AppShare[]>([])
  const [resources, setResources] = useState<MergedAppResources | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showAllShares, setShowAllShares] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false)
  const [publishPreflight, setPublishPreflight] = useState<SourcePreflightResponse | null>(null)
  const [publishPreflightError, setPublishPreflightError] = useState<string | null>(null)
  const [focusWorkRequest, setFocusWorkRequest] = useState(0)
  const [showRebuildConfirm, setShowRebuildConfirm] = useState(false)
  const [publishAccessLevel, setPublishAccessLevel] = useState<string>('platform')
  const [showResources, setShowResources] = useState(false)
  const [copied, setCopied] = useState(false)
  const [logs, setLogs] = useState<string>('')
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsExpanded, setLogsExpanded] = useState(false)
  const [logTab, setLogTab] = useState<'all' | 'build' | 'runtime'>('all')
  const [actionError, setActionError] = useState<string | null>(null)
  const [cloneNotice, setCloneNotice] = useState<CloneNotice | null>(null)
  const [showEditInfo, setShowEditInfo] = useState(false)
  const [deploymentPhase, setDeploymentPhase] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<{
    log_excerpt?: string; port?: number; resolution?: string
  } | null>(null)

  // Production build state
  const [productionStatus, setProductionStatus] = useState<string | null>(null)
  const [productionError, setProductionError] = useState<string | null>(null)
  const [productionLogs, setProductionLogs] = useState<{ build_logs: string; runtime_logs: string } | null>(null)
  const [productionLogsExpanded, setProductionLogsExpanded] = useState(false)
  const [productionLogsLoading, setProductionLogsLoading] = useState(false)
  const [copiedDevUrl, setCopiedDevUrl] = useState(false)
  const [appSecrets, setAppSecrets] = useState<AppSecretMetadata[]>([])
  const [requiredAppSecrets, setRequiredAppSecrets] = useState<RequiredAppSecret[]>([])
  const [secretsLoading, setSecretsLoading] = useState(false)
  const [secretSavingKey, setSecretSavingKey] = useState<string | null>(null)
  const [secretDeleting, setSecretDeleting] = useState<string | null>(null)
  const [secretError, setSecretError] = useState<string | null>(null)
  const [secretSuccess, setSecretSuccess] = useState<string | null>(null)
  const [showManualSecretEditor, setShowManualSecretEditor] = useState(false)
  const [requiredSecretDrafts, setRequiredSecretDrafts] = useState<Record<string, string>>({})
  const [secretNameInput, setSecretNameInput] = useState('')
  const [secretValueInput, setSecretValueInput] = useState('')
  const [secretDescriptionInput, setSecretDescriptionInput] = useState('')

  // Access requests state
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([])
  const [requestActionLoading, setRequestActionLoading] = useState<number | null>(null)

  const loadApp = useCallback(async () => {
    if (!appName) return
    try {
      const found = await fetchApp(appName)
      setApp(found)

      // Only fetch shares/resources for users who can edit (avoids unnecessary 403s)
      const myPerm = found.my_permission ?? 'owner'
      const canEditApp = ['owner', 'admin', 'edit'].includes(myPerm)

      if (canEditApp) {
        const [sharesData, resourcesData] = await Promise.all([
          listAppShares(appName).catch(() => []),
          getAppResources(appName).catch(() => null),
        ])
        setShares(sharesData)
        if (resourcesData) setResources(resourcesData)
      } else {
        setShares([])
        setResources(null)
      }
    } catch (err) {
      console.error('Failed to load app:', err)
      setApp(null)
    } finally {
      setLoading(false)
    }
  }, [appName])

  useEffect(() => {
    loadApp()
  }, [loadApp])

  // Load access requests for owners
  const loadAccessRequests = useCallback(async () => {
    if (!appName) return
    try {
      const requests = await listAccessRequests()
      setAccessRequests(requests.filter(r => r.app_name === appName))
    } catch {
      // Silently ignore - user may not be owner
    }
  }, [appName])

  useEffect(() => {
    loadAccessRequests()
  }, [loadAccessRequests])

  useEffect(() => {
    const state = location.state as { cloneNotice?: CloneNotice } | null
    if (!state?.cloneNotice) return

    setCloneNotice(state.cloneNotice)
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: null }
    )
  }, [location.hash, location.pathname, location.search, location.state, navigate])

  async function handleApproveRequest(requestId: number) {
    setRequestActionLoading(requestId)
    try {
      await approveAccessRequest(requestId)
      setAccessRequests(prev => prev.filter(r => r.id !== requestId))
      await loadApp() // Refresh shares
    } catch (err) {
      console.error('Failed to approve request:', err)
    } finally {
      setRequestActionLoading(null)
    }
  }

  async function handleDenyRequest(requestId: number) {
    setRequestActionLoading(requestId)
    try {
      await denyAccessRequest(requestId)
      setAccessRequests(prev => prev.filter(r => r.id !== requestId))
    } catch (err) {
      console.error('Failed to deny request:', err)
    } finally {
      setRequestActionLoading(null)
    }
  }

  // Compute the display phase: prefer polled phase > app.deployment_phase > app.status
  const displayPhase = deploymentPhase || app?.deployment_phase || app?.status || 'stopped'
  const isDeploying = ['queued', 'building', 'installing', 'starting'].includes(displayPhase)
  const resolvedErrorMessage = errorMessage || app?.error_message || null
  const errorTitle = resolvedErrorMessage?.toLowerCase().startsWith('app restart failed')
    ? 'Restart Error'
    : 'Deployment Error'

  // Compute production display status
  const prodStatus = productionStatus || app?.production_status || null
  const isProductionBuilding = prodStatus === 'building'
  const publishSourceSummary = publishPreflight?.sourceSummary ?? null
  const publishChangedFileCount = publishSourceSummary?.changedFileCount ?? null
  const publishSavedVersion = publishSourceSummary?.savedVersionId ?? null
  const publishHasUnsavedChanges = Boolean(publishSourceSummary?.hasUnsavedChanges ?? false)

  // Poll deployment status during active deployment or production build
  const pollDeploymentStatus = useCallback(async () => {
    if (!appName) return
    try {
      const result: DeploymentStatus = await getDeploymentStatus(appName)
      setDeploymentPhase(result.deployment_phase)
      setErrorMessage(result.error_message)
      setErrorDetails(result.error_details)

      // Update production status from deployment-status response
      if (result.production_status !== undefined) {
        setProductionStatus(result.production_status ?? null)
        setProductionError(result.production_error_message ?? null)
      }

      // When deployment settles, refresh full app data and stop polling
      if (result.deployment_phase === 'running' || result.deployment_phase === 'error') {
        await loadApp()
        // Keep the phase set so UI shows final state until next action
      }

      // When production build settles, refresh full app data
      if (result.production_status && result.production_status !== 'building') {
        await loadApp()
      }
    } catch (err) {
      console.error('Failed to poll deployment status:', err)
      throw err
    }
  }, [appName, loadApp])

  useAutoRefresh(pollDeploymentStatus, {
    baseInterval: 2000,
    maxInterval: 5000,
    enabled: isDeploying || isProductionBuilding,
  })

  // Auto-expand logs on error and switch to build tab
  useEffect(() => {
    if (displayPhase === 'error') {
      setLogsExpanded(true)
      setLogTab('build')
    }
  }, [displayPhase])

  // Fetch logs (supports log type tabs)
  const fetchLogs = useCallback(async () => {
    if (!appName || !app) return
    const canFetchLogs = app.status === 'running' || displayPhase === 'error' || isDeploying
    if (!canFetchLogs) return
    setLogsLoading(true)
    try {
      const result = await getAppLogs(appName, 100, logTab)
      setLogs(result.logs || '')
    } catch (err) {
      console.error('Failed to fetch logs:', err)
      setLogs('')
      throw err
    } finally {
      setLogsLoading(false)
    }
  }, [appName, app, logTab, displayPhase, isDeploying])

  // Re-fetch logs when tab changes
  useEffect(() => {
    if (logsExpanded && app) {
      fetchLogs()
    }
  }, [logTab, logsExpanded, fetchLogs, app])

  // Initial log fetch when conditions become true
  useEffect(() => {
    if ((app?.status === 'running' || displayPhase === 'error') && logsExpanded) {
      fetchLogs()
    }
  }, [app?.status, displayPhase, logsExpanded, fetchLogs])

  // Log polling with exponential backoff
  useAutoRefresh(fetchLogs, {
    baseInterval: 5000,
    maxInterval: 60000,   // 1 minute max backoff
    enabled: (app?.status === 'running' || displayPhase === 'error') && logsExpanded,
  })

  async function handleStart() {
    if (!app) return
    setActionLoading('start')
    setActionError(null)
    setDeploymentPhase('queued')
    setErrorMessage(null)
    setErrorDetails(null)
    try {
      const result = await startApp(app.name)
      if (result.deployment_phase) {
        setDeploymentPhase(result.deployment_phase)
      }
      await loadApp()
    } catch (err) {
      setDeploymentPhase(null)
      setActionError(err instanceof Error ? err.message : 'Failed to start app')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleStop() {
    if (!app) return
    setActionLoading('stop')
    setActionError(null)
    try {
      await stopApp(app.name)
      setDeploymentPhase(null)
      setErrorMessage(null)
      setErrorDetails(null)
      await loadApp()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to stop app')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleRebuild() {
    if (!app) return
    setActionLoading('rebuild')
    setActionError(null)
    setDeploymentPhase('queued')
    setErrorMessage(null)
    setErrorDetails(null)
    try {
      const result = await rebuildApp(app.name)
      if (result.deployment_phase) {
        setDeploymentPhase(result.deployment_phase)
      }
      await loadApp()
    } catch (err) {
      setDeploymentPhase(null)
      setActionError(err instanceof Error ? err.message : 'Failed to rebuild app')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDelete() {
    if (!app) return
    setActionLoading('delete')
    try {
      await deleteApp(app.name)
      navigate('/apps')
    } catch (err) {
      console.error('Failed to delete app:', err)
      setActionLoading(null)
    }
  }

  async function requestMakeLive() {
    if (!app || isProductionBuilding) return

    setActionLoading('publish-preflight')
    setActionError(null)
    try {
      const response = await preflightMakeAppLive(app.name)
      setPublishPreflight(response)
      setPublishPreflightError(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'The safety check could not run.'
      setPublishPreflight(null)
      setPublishPreflightError(message)
    } finally {
      setActionLoading(null)
    }

    setPublishAccessLevel('platform')
    setShowPublishConfirm(true)
  }

  async function handleProductionBuild(accessLevel?: string) {
    if (!app) return
    setActionLoading('production-build')
    setActionError(null)
    setProductionStatus('building')
    setProductionError(null)

    // Update access level if changed
    const newAccessLevel = accessLevel || publishAccessLevel
    if (newAccessLevel !== app.access_level) {
      try {
        await updateApp(app.name, { access_level: newAccessLevel } as Partial<App>)
      } catch (err) {
        console.error('Failed to update access level:', err)
      }
    }

    try {
      await triggerProductionBuild(app.name)
      await loadApp()
    } catch (err) {
      setProductionStatus(null)
      setActionError(err instanceof Error ? err.message : 'Failed to start production build')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleStopProduction() {
    if (!app) return
    setActionLoading('production-stop')
    setActionError(null)
    try {
      await stopProductionBuild(app.name)
      setProductionStatus('stopped')
      await loadApp()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to stop production')
    } finally {
      setActionLoading(null)
    }
  }

  async function fetchProductionLogs() {
    if (!appName) return
    setProductionLogsLoading(true)
    try {
      const result = await getProductionLogs(appName)
      setProductionLogs({ build_logs: result.build_logs, runtime_logs: result.runtime_logs })
    } catch (err) {
      console.error('Failed to fetch production logs:', err)
      setProductionLogs(null)
    } finally {
      setProductionLogsLoading(false)
    }
  }

  function copyUrl() {
    if (!app) return
    navigator.clipboard.writeText(app.url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function copyDevPreviewUrl() {
    if (!app?.dev_preview_url) return
    navigator.clipboard.writeText(app.dev_preview_url)
    setCopiedDevUrl(true)
    setTimeout(() => setCopiedDevUrl(false), 2000)
  }

  const loadSecrets = useCallback(async () => {
    if (!app) return
    const isOwner = app.is_owner ?? true
    const canManage = isOwner || user.isAdmin
    if (!canManage) {
      setAppSecrets([])
      setRequiredAppSecrets([])
      return
    }

    setSecretsLoading(true)
    setSecretError(null)
    try {
      const result = await listAppSecrets(app.name)
      setAppSecrets(result.secrets || [])
      setRequiredAppSecrets(result.required_secrets || [])
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to load secrets')
    } finally {
      setSecretsLoading(false)
    }
  }, [app, user.isAdmin])

  useEffect(() => {
    setSecretSuccess(null)
    setSecretError(null)
    setRequiredSecretDrafts({})
    setShowManualSecretEditor(false)
    setSecretNameInput('')
    setSecretValueInput('')
    setSecretDescriptionInput('')
  }, [appName])

  useEffect(() => {
    if (app) {
      loadSecrets()
    }
  }, [app, loadSecrets])

  async function handleSaveSecret() {
    if (!app) return
    const key = secretNameInput.trim()
    if (!key || !secretValueInput) {
      setSecretError('Secret name and value are required')
      return
    }

    setSecretSavingKey('__manual__')
    setSecretError(null)
    setSecretSuccess(null)
    try {
      await upsertAppSecret(app.name, key, {
        value: secretValueInput,
        description: secretDescriptionInput.trim() || undefined,
      })
      setSecretSuccess(`Secret "${key}" saved`)
      setSecretNameInput('')
      setSecretValueInput('')
      setSecretDescriptionInput('')
      await loadSecrets()
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to save secret')
    } finally {
      setSecretSavingKey(null)
    }
  }

  function setRequiredSecretDraft(secretName: string, value: string) {
    setRequiredSecretDrafts((current) => ({
      ...current,
      [secretName]: value,
    }))
  }

  async function handleSaveRequiredSecret(secret: RequiredAppSecret) {
    if (!app) return
    const draftValue = requiredSecretDrafts[secret.name]
    if (typeof draftValue !== 'string' || draftValue.length === 0) {
      setSecretError(`Enter a value for ${secret.display_name || secret.name}`)
      return
    }

    setSecretSavingKey(secret.name)
    setSecretError(null)
    setSecretSuccess(null)
    try {
      await upsertAppSecret(app.name, secret.name, {
        value: draftValue,
        description: secret.description || undefined,
      })
      setSecretSuccess(`${secret.display_name || secret.name} saved`)
      setRequiredSecretDrafts((current) => ({
        ...current,
        [secret.name]: '',
      }))
      await loadSecrets()
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to save secret')
    } finally {
      setSecretSavingKey(null)
    }
  }

  async function handleDeleteSecret(secretName: string) {
    if (!app) return
    const confirmed = window.confirm(`Delete secret "${secretName}"?`)
    if (!confirmed) return

    setSecretDeleting(secretName)
    setSecretError(null)
    setSecretSuccess(null)
    try {
      await deleteAppSecret(app.name, secretName)
      setSecretSuccess(`Secret "${secretName}" deleted`)
      await loadSecrets()
    } catch (err) {
      setSecretError(err instanceof Error ? err.message : 'Failed to delete secret')
    } finally {
      setSecretDeleting(null)
    }
  }

  const {
    folders,
    addAppToFolder,
    removeAppFromFolder,
    createFolderAndAddApp,
    getAppFolderIds,
  } = useFolders(app ? [app] : [], 'my-apps')

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!app) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-semibold mb-2">App not found</h2>
        <p className="text-muted-foreground mb-4">The app "{appName}" doesn't exist or you don't have access.</p>
        <Link to="/apps" className="text-primary hover:underline">
          Back to My Apps
        </Link>
      </div>
    )
  }

  // Access Required page for unauthorized users
  if (app.access_denied) {
    const handleAccessRequest = async () => {
      try {
        await requestAccess(app.name)
        await loadApp()
      } catch (err) {
        console.error('Failed to request access:', err)
      }
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-6 flex items-center justify-center">
            <Lock className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Access Required</h1>
          <p className="text-muted-foreground mb-2">
            You need permission to access this app
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            {app.display_name || app.name}
            {app.owner_name && <> by {app.owner_name}</>}
          </p>
          {app.access_level === 'specific' && !app.has_pending_request && (
            <button
              onClick={handleAccessRequest}
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Request Access
            </button>
          )}
          {app.has_pending_request && (
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>Access request pending</span>
            </div>
          )}
          {app.access_level === 'personal' && (
            <p className="text-sm text-muted-foreground">
              This is a private app
            </p>
          )}
          <button
            onClick={() => navigate('/apps')}
            className="mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Go back
          </button>
        </div>
      </div>
    )
  }

  const status = statusConfig[displayPhase] || statusConfig.stopped
  const access = accessConfig[app.access_level as keyof typeof accessConfig] || accessConfig.personal
  const runtime = runtimeConfig[app.runtime as keyof typeof runtimeConfig] || runtimeConfig.auto
  const AccessIcon = access.icon
  // In the new per-app workspace container, the app code is at /home/coder/workspace/main
  const appFolder = `/home/coder/workspace/main`

  // Permission-based UI control
  const isOwner = app.is_owner ?? true  // Default to true for backwards compatibility
  const myPermission = app.my_permission ?? 'owner'  // Default to owner for backwards compatibility
  const canEdit = ['owner', 'admin', 'edit'].includes(myPermission)
  const canManageShares = canEdit  // Owner, admin, and editors can manage shares
  const canDelete = isOwner  // Only owner can delete
  const canManageSecrets = isOwner || user.isAdmin
  const actionPermission: 'owner' | 'admin' | 'edit' | 'view' =
    myPermission === 'owner' || isOwner ? 'owner'
    : myPermission === 'admin' || myPermission === 'edit' || myPermission === 'view' ? myPermission
    : 'view'

  // Permission label for non-owners
  const permissionLabels: Record<string, { label: string; color: string }> = {
    admin: { label: 'Admin Access', color: 'text-purple-500 bg-purple-500/10' },
    edit: { label: 'Edit Access', color: 'text-blue-500 bg-blue-500/10' },
    view: { label: 'View Only', color: 'text-muted-foreground bg-muted' },
    platform: { label: 'Platform Access', color: 'text-muted-foreground bg-muted' },
  }
  const permInfo = !isOwner ? permissionLabels[myPermission] : null

  return (
    <div>
      {/* Header — single row with flex-wrap: back + title + (actions + Open App) stay together */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate('/apps')}
          className="p-2 rounded-lg hover:bg-secondary transition-colors shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0 sm:min-w-[360px]">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
            {app.url ? (
              <a href={app.url} target="_blank" rel="noopener noreferrer" className="text-xl sm:text-2xl font-bold hover:text-primary hover:underline transition-colors truncate">
                {app.display_name || app.name}
              </a>
            ) : (
              <h1 className="text-xl sm:text-2xl font-bold truncate">{app.display_name || app.name}</h1>
            )}
            {canEdit && (
              <button title="Edit app name, description, and category" onClick={() => setShowEditInfo(true)} className="p-1 sm:p-1.5 rounded-lg hover:bg-secondary transition-colors shrink-0">
                <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground" />
              </button>
            )}
            {app.is_template && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-500 border border-indigo-500/20">
                <LayoutTemplate className="w-3.5 h-3.5" />
                Template
              </span>
            )}
            <div className="shrink-0">
              <InlineCategoryPicker
                currentCategory={app.category || null}
                canEdit={canEdit}
                onCategoryChange={async (cat) => { await updateApp(app.name, { category: cat }); await loadApp() }}
              />
            </div>
            {permInfo && (
              <span className={`px-2 py-0.5 sm:py-1 rounded text-xs font-medium shrink-0 ${permInfo.color}`}>{permInfo.label}</span>
            )}
          </div>
          <p className="text-muted-foreground font-mono text-xs sm:text-sm truncate">/a/{app.name}/</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={app.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
            <ExternalLink className="w-4 h-4" />
            Open App
          </a>
          <AppActions
            app={app}
            permission={actionPermission}
            onStart={canEdit ? handleStart : undefined}
            onStop={canEdit ? handleStop : undefined}
            onRebuild={canEdit ? async () => { setShowRebuildConfirm(true) } : undefined}
            onRename={canEdit ? () => setShowEditInfo(true) : undefined}
            onPublish={canEdit ? () => { void requestMakeLive() } : undefined}
            onClone={canEdit ? () => setShowCloneModal(true) : undefined}
            onShare={canEdit ? () => setShowShareModal(true) : undefined}
            onDelete={canDelete ? handleDelete : undefined}
            folders={folders}
            appFolderIds={getAppFolderIds(app.name)}
            onMoveToFolder={(folderId) => { void addAppToFolder(folderId, app.name) }}
            onRemoveFromFolder={(folderId) => { void removeAppFromFolder(folderId, app.name) }}
            onCreateFolderAndMove={(name) => { void createFolderAndAddApp(name, app.name) }}
            showDetails={false}
            showLabels
          />
        </div>
      </div>

      {actionError && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 flex items-center justify-between">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="p-1 hover:bg-destructive/20 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {cloneNotice && (
        <div
          className={`mb-6 p-4 rounded-lg border flex items-center justify-between gap-3 ${
            cloneNotice.tone === 'warning'
              ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20'
              : 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20'
          }`}
        >
          <div className="flex items-center gap-2.5">
            <Info className="w-4.5 h-4.5 shrink-0" />
            <span>{cloneNotice.message}</span>
          </div>
          <button
            onClick={() => setCloneNotice(null)}
            className="p-1 hover:bg-black/5 dark:hover:bg-white/10 rounded"
            aria-label="Dismiss clone notice"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Access Request Notification Banner */}
      {canManageShares && !app.is_template && accessRequests.length > 0 && (
        <div className="mb-6 p-3.5 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Bell className="w-4.5 h-4.5 text-amber-500 shrink-0" />
            <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
              {accessRequests.length} pending access {accessRequests.length === 1 ? 'request' : 'requests'}
            </span>
            <span className="text-xs text-amber-600/70 dark:text-amber-400/70 hidden sm:inline">
              — {accessRequests.map(r => r.requester_name || r.requester_email).join(', ')}
            </span>
          </div>
          <a
            href="#sharing"
            onClick={(e) => {
              e.preventDefault()
              document.querySelector('[data-section="sharing"]')?.scrollIntoView({ behavior: 'smooth' })
            }}
            className="text-xs font-medium text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 whitespace-nowrap"
          >
            Review requests &rarr;
          </a>
        </div>
      )}

      {/* Template info banner */}
      {app.is_template && canEdit && (
        <div className="mb-6 p-3.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <LayoutTemplate className="w-4.5 h-4.5 text-indigo-500 shrink-0" />
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-400">
              This app is a template in the gallery
            </span>
          </div>
          <Link
            to="/admin/templates"
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 whitespace-nowrap"
          >
            Edit Template Info &rarr;
          </Link>
        </div>
      )}

      {/* App not running banner for viewers */}
      {!canEdit && app.status !== 'running' && (
        <div className="mb-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/10">
          <p className="text-sm text-amber-600 font-medium">
            This app is not currently running. The owner can start it from their dashboard.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {app.description ? (
            <div className="p-6 rounded-xl border border-border bg-card">
              <h2 className="text-lg font-semibold mb-2">Description</h2>
              <p className="text-muted-foreground whitespace-pre-wrap">{app.description}</p>
            </div>
          ) : canEdit ? (
            <button
              onClick={() => setShowEditInfo(true)}
              className="w-full p-4 rounded-xl border border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors text-sm"
            >
              + Add a description
            </button>
          ) : null}

          {/* Status Card */}
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4">Status</h2>
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className={`w-4 h-4 rounded-full ${status.color}`} />
                {status.pulse && (
                  <div className={`absolute inset-0 w-4 h-4 rounded-full ${status.color} animate-ping`} />
                )}
              </div>
              <div>
                <p className={`font-semibold ${status.textColor}`}>{status.label}</p>
                <p className="text-sm text-muted-foreground">
                  {displayPhase === 'error'
                    ? (errorMessage || app.error_message || 'Deployment failed')
                    : (statusDescriptions[displayPhase] || 'App is not running')}
                </p>
              </div>
            </div>

            {/* Deployment Progress Stepper */}
            {(isDeploying || displayPhase === 'error') && (
              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  {deploymentSteps.map((step, i) => {
                    const stepIndex = deploymentSteps.indexOf(displayPhase as typeof deploymentSteps[number])
                    const errorIndex = displayPhase === 'error'
                      ? deploymentSteps.indexOf(
                          (deploymentPhase && deploymentPhase !== 'error'
                            ? deploymentPhase
                            : app?.deployment_phase && app.deployment_phase !== 'error'
                              ? app.deployment_phase
                              : 'starting') as typeof deploymentSteps[number]
                        )
                      : -1
                    const activeIndex = displayPhase === 'error' ? errorIndex : stepIndex
                    const isComplete = i < activeIndex
                    const isCurrent = i === activeIndex
                    const isError = displayPhase === 'error' && isCurrent

                    return (
                      <div key={step} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${
                            isError ? 'bg-red-500' :
                            isComplete ? 'bg-green-500' :
                            isCurrent ? 'bg-blue-500 animate-pulse' :
                            'bg-gray-600'
                          }`} />
                          <span className={`text-[10px] mt-1 ${
                            isError ? 'text-red-500' :
                            isCurrent ? 'text-foreground font-medium' :
                            isComplete ? 'text-green-500' :
                            'text-muted-foreground'
                          }`}>
                            {step.charAt(0).toUpperCase() + step.slice(1)}
                          </span>
                        </div>
                        {i < deploymentSteps.length - 1 && (
                          <div className={`h-0.5 flex-1 mx-1 ${
                            isComplete ? 'bg-green-500' : 'bg-gray-700'
                          }`} />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Error Guidance Panel */}
          {displayPhase === 'error' && resolvedErrorMessage && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <h3 className="font-semibold text-red-500 mb-2">{errorTitle}</h3>
              <p className="text-sm text-muted-foreground mb-3">
                {resolvedErrorMessage}
              </p>
              {(errorDetails?.resolution || app.error_details?.resolution) && (
                <div className="p-3 rounded bg-muted/50">
                  <p className="text-sm font-medium mb-1">How to fix:</p>
                  <p className="text-sm text-muted-foreground">
                    {errorDetails?.resolution || app.error_details?.resolution}
                  </p>
                </div>
              )}
              {(errorDetails?.log_excerpt || app.error_details?.log_excerpt) && (
                <div className="mt-3 p-3 rounded bg-[#1a1a1a] font-mono text-xs max-h-32 overflow-y-auto">
                  <pre className="text-gray-300 whitespace-pre-wrap">
                    {errorDetails?.log_excerpt || app.error_details?.log_excerpt}
                  </pre>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-red-500/10">
                <HelpLink section="deployment">View deployment guide</HelpLink>
              </div>
            </div>
          )}

          {canEdit && (
            <SourceReleasePanel
              appName={app.name}
              appFolder={appFolder}
              canEdit={canEdit}
              liveStatus={prodStatus}
              liveError={productionError || app.production_error_message || null}
              lastSuccessfulBuild={app.last_successful_build}
              actionLoading={actionLoading}
              liveLogs={productionLogs}
              liveLogsExpanded={productionLogsExpanded}
              liveLogsLoading={productionLogsLoading}
              onOpenSession={(terminalUrl, chromeUrl) => {
                const workspacePath = withMountBase('/workspace', window.location.pathname)
                const appPath = withMountBase(`/a/${app.name}/`, window.location.pathname)
                const appUrl = `${window.location.origin}${appPath}`
                // Honor the chromeUrl returned by openAppSession (D-22, D-23 / UX-11) so the
                // workspace panel can open the embedded chrome on first render.
                const chromeParam = chromeUrl
                  ? `&chromeUrl=${encodeURIComponent(chromeUrl)}`
                  : ''
                window.location.href = `${workspacePath}?app=${encodeURIComponent(app.name)}&mode=terminal&folder=${encodeURIComponent(appFolder)}&url=${encodeURIComponent(appUrl)}&session=${encodeURIComponent(terminalUrl)}${chromeParam}`
              }}
              onStopLive={handleStopProduction}
              onToggleLiveLogs={() => {
                const newExpanded = !productionLogsExpanded
                setProductionLogsExpanded(newExpanded)
                if (newExpanded) {
                  fetchProductionLogs()
                }
              }}
              formatRelativeTime={formatRelativeTime}
              focusWorkRequest={focusWorkRequest}
            />
          )}

          {/* Container Info - only for edit users */}
          {canEdit && (
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Server className="w-5 h-5" />
              Container
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  displayPhase === 'running' ? 'bg-green-500/20 text-green-500' :
                  isDeploying ? 'bg-amber-500/20 text-amber-500' :
                  displayPhase === 'error' ? 'bg-red-500/20 text-red-500' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {status.label}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Container Name</span>
                <span className="font-mono text-sm text-primary">cc-app-{app.name}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Port</span>
                <span className="font-mono text-sm">{app.port}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Runtime</span>
                <span className="text-sm font-medium">{runtime.label}</span>
              </div>

              {/* Resource Allocation */}
              {resources && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Cpu className="w-3 h-3" /> CPU
                    </span>
                    <span className="font-mono text-sm">{resources.cpu_limit}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <HardDrive className="w-3 h-3" /> Memory
                    </span>
                    <span className="font-mono text-sm">{resources.memory_limit}</span>
                  </div>
                </>
              )}

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <GitBranch className="w-3 h-3" /> Git
                </span>
                <span className={`text-sm ${app.git_initialized ? 'text-green-500' : 'text-muted-foreground'}`}>
                  {app.git_initialized ? 'Initialized' : 'Not initialized'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Created
                </span>
                <span className="text-sm">{new Date(app.created_at).toLocaleDateString()}</span>
              </div>

              {app.last_accessed && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Last Accessed
                  </span>
                  <span className="text-sm">{formatRelativeTime(app.last_accessed)}</span>
                </div>
              )}

              {/* Server Logs with Tabs */}
              <div className="mt-4 pt-4 border-t border-border">
                <button
                  onClick={() => {
                    setLogsExpanded(!logsExpanded)
                    if (!logsExpanded) {
                      fetchLogs()
                    }
                  }}
                  className="flex items-center justify-between w-full mb-2"
                >
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Server Logs
                  </span>
                  <div className="flex items-center gap-2">
                    {logsLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                    {logsExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>
                </button>
                {logsExpanded && (
                  <>
                    {/* Log Type Tabs */}
                    <div className="flex gap-1 mb-2">
                      {(['all', 'build', 'runtime'] as const).map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setLogTab(tab)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            logTab === tab
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {tab === 'all' ? 'All Logs' : tab === 'build' ? 'Build' : 'Runtime'}
                        </button>
                      ))}
                    </div>
                    <div className="p-4 rounded-lg bg-[#1a1a1a] font-mono text-xs max-h-64 overflow-y-auto">
                      {displayPhase === 'stopped' && !logs ? (
                        <span className="text-gray-500">App is not running</span>
                      ) : logs ? (
                        <pre className="text-gray-300 whitespace-pre-wrap">{logs}</pre>
                      ) : logsLoading ? (
                        <span className="text-gray-500">Loading logs...</span>
                      ) : (
                        <span className="text-gray-500">No logs available</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          )}

          {/* App Files - only for edit users */}
          {canEdit && (
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              App Files
            </h2>
            <FileBrowserPanel
              standalone={true}
              appName={app.name}
            />
          </div>
          )}

          {/* Visitor Trends */}
          {appName && <div><VisitorTrendChart appName={appName} /></div>}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-lg font-semibold mb-4">Details</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Runtime</p>
                  <p className="font-medium">{runtime.label}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <GitBranch className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Git</p>
                  <p className="font-medium">{app.git_initialized ? 'Initialized' : 'Not initialized'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">{new Date(app.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              {app.last_accessed && (
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Last accessed</p>
                    <p className="font-medium">{new Date(app.last_accessed).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* URL */}
          <div className="p-6 rounded-xl border border-border bg-card">
            {prodStatus === 'running' && app.production_url ? (
              <>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  URLs
                </h2>
                {/* Production URL (primary) */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-green-500 mb-1 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                    App URL
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={app.url}
                      readOnly
                      className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-sm font-mono"
                    />
                    
                      <a title="Open app URL in a new tab"
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    
                    
                      <button title="Copy app URL to clipboard"
                        onClick={copyUrl}
                        className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    
                  </div>
                </div>
                {/* Dev Preview URL - only for edit users */}
                {canEdit && app.dev_preview_url && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                      Dev Preview URL <span className="text-xs text-muted-foreground font-normal">(changes visible here)</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={app.dev_preview_url}
                        readOnly
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-sm font-mono"
                      />
                      
                        <a title="Open the development preview in a new tab"
                          href={app.dev_preview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      
                      
                        <button title="Copy the dev preview URL to clipboard"
                          onClick={copyDevPreviewUrl}
                          className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                        >
                          {copiedDevUrl ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold mb-4">App URL</h2>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={app.url}
                    readOnly
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-secondary text-sm font-mono"
                  />
                  
                    <button title="Copy this app URL to clipboard"
                      onClick={copyUrl}
                      className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  
                </div>
              </>
            )}
          </div>

          {/* Sharing - only for edit users, hidden for templates */}
          {canEdit && !app.is_template && (
          <div data-section="sharing" className="p-6 rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Sharing</h2>
              {canManageShares && (
                <button
                  onClick={() => setShowShareModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border border-border hover:bg-secondary transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                <AccessIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">{access.label}</p>
                <p className="text-sm text-muted-foreground">{access.description}</p>
              </div>
            </div>
            {shares.length > 0 && canManageShares && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">Shared with:</p>
                <div className="space-y-2">
                  {(showAllShares ? shares : shares.slice(0, 5)).map((share) => (
                    <div key={share.id} className="flex items-center justify-between text-sm">
                      <span>{share.email}</span>
                      <span className="text-muted-foreground">{share.permission}</span>
                    </div>
                  ))}
                </div>
                {shares.length > 5 && (
                  <button
                    onClick={() => setShowAllShares(!showAllShares)}
                    className="mt-2 text-xs text-primary hover:underline"
                  >
                    {showAllShares ? 'Show less' : `Show ${shares.length - 5} more`}
                  </button>
                )}
              </div>
            )}
            {canManageShares && accessRequests.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="w-4 h-4 text-amber-500" />
                  <p className="text-sm font-medium">
                    Access Requests
                    <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                      {accessRequests.length}
                    </span>
                  </p>
                </div>
                <div className="space-y-2">
                  {accessRequests.map((req) => (
                    <div key={req.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-secondary/50">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{req.requester_name || req.requester_email}</p>
                        {req.requester_name && (
                          <p className="text-xs text-muted-foreground truncate">{req.requester_email}</p>
                        )}
                        <p className="text-[11px] text-muted-foreground">{formatRelativeTime(req.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => handleApproveRequest(req.id)}
                          disabled={requestActionLoading === req.id}
                          className="px-2.5 py-1 text-xs rounded-md bg-green-500/10 text-green-600 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                          title="Approve"
                        >
                          {requestActionLoading === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleDenyRequest(req.id)}
                          disabled={requestActionLoading === req.id}
                          className="px-2.5 py-1 text-xs rounded-md bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                          title="Deny"
                        >
                          Deny
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Resources - only for edit users */}
          {canEdit && (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <button
              onClick={() => setShowResources(!showResources)}
              className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Server className="w-5 h-5 text-primary" />
                <div className="text-left">
                  <h3 className="font-semibold">Resources</h3>
                  <p className="text-sm text-muted-foreground">
                    {resources?.is_customized ? 'Custom configuration' : 'Using defaults'}
                  </p>
                </div>
              </div>
              {showResources ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
            </button>
            {showResources && resources && (
              <div className="p-4 pt-0 border-t border-border">
                <div className="space-y-3 pt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Cpu className="w-4 h-4" /> CPU
                    </span>
                    <span>{resources.cpu_request} / {resources.cpu_limit}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <HardDrive className="w-4 h-4" /> Memory
                    </span>
                    <span>{resources.memory_request} / {resources.memory_limit}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Replicas</span>
                    <span>{resources.replicas_min} - {resources.replicas_max}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Disk</span>
                    <span>{resources.disk_gb} GB</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Health Checks</span>
                    <span>{resources.health_check_enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Auto Restart</span>
                    <span>{resources.auto_restart ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
                {resources.is_customized && (
                  <button
                    onClick={async () => {
                      if (!app) return
                      await resetAppResources(app.name)
                      loadApp()
                    }}
                    className="flex items-center gap-2 mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset to defaults
                  </button>
                )}
              </div>
            )}
          </div>
          )}

          {/* Keep Alive - Only shown to users with edit permission */}
          {canEdit && (
            <div className="p-6 rounded-xl border border-border bg-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-primary" />
                  <div>
                    <h3 className="font-semibold">Keep alive</h3>
                    <p className="text-sm text-muted-foreground">
                      Prevent auto-shutdown after inactivity
                    </p>
                  </div>
                </div>
                <Switch
                  checked={app.keep_alive}
                  onCheckedChange={async (checked) => {
                    try {
                      const updated = await updateApp(app.name, { keep_alive: checked })
                      setApp(updated)
                    } catch (err) {
                      console.error('Failed to update keep alive:', err)
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Secrets - only for app owner or platform admin */}
          {canManageSecrets && (
            <div className="p-6 rounded-xl border border-border bg-card">
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Key className="w-5 h-5 text-primary" />
                Secrets
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Encrypted per-app secrets. Values are write-only and never shown again.
              </p>
              <div className="mb-4 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
                <p>Reference these in <code className="font-mono">cc-app.json</code> under <code className="font-mono">env</code>:</p>
                <p><code className="font-mono">MY_TOKEN=${'{secret:MY_TOKEN}'}</code></p>
                <p><code className="font-mono">TLS_CERT_PATH=${'{secret-file:TLS_CERT:/run/secrets/tls-cert.pem}'}</code></p>
              </div>

              {secretError && (
                <div className="mb-3 p-3 rounded-lg bg-destructive/10 text-destructive text-sm border border-destructive/20">
                  {secretError}
                </div>
              )}
              {secretSuccess && (
                <div className="mb-3 p-3 rounded-lg bg-green-500/10 text-green-500 text-sm border border-green-500/20">
                  {secretSuccess}
                </div>
              )}

              <div className="pt-4 border-t border-border">
                <h3 className="text-sm font-medium mb-2">Required by manifest</h3>
                {requiredAppSecrets.length === 0 ? (
                  <p className="text-sm text-muted-foreground mb-4">
                    No manifest-declared app secrets detected.
                  </p>
                ) : (
                  <div className="space-y-2 mb-4">
                    {requiredAppSecrets.map((secret) => (
                      <div key={secret.name} className="p-3 rounded-lg border border-border space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">
                              {secret.display_name || secret.name}
                            </p>
                            <p className="font-mono text-[11px] text-muted-foreground break-all mt-1">
                              {secret.name}
                            </p>
                            {secret.description && (
                              <p className="text-xs text-muted-foreground mt-2">
                                {secret.description}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {secret.kind === 'file' && secret.mount_path
                                ? `Mounted as a file at ${secret.mount_path}`
                                : 'Injected as an environment variable'}
                            </p>
                            {secret.referenced_by.length > 0 && (
                              <p className="text-[11px] text-muted-foreground mt-1 break-all">
                                Referenced by {secret.referenced_by.join(', ')}
                              </p>
                            )}
                          </div>
                          <span
                            className={`px-2 py-1 rounded-md text-[11px] border ${
                              secret.is_set
                                ? 'border-green-500/30 text-green-500 bg-green-500/10'
                                : 'border-amber-500/30 text-amber-500 bg-amber-500/10'
                            }`}
                          >
                            {secret.is_set ? 'Configured' : 'Missing'}
                          </span>
                        </div>

                        <div className="space-y-2">
                          {secret.kind === 'file' ? (
                            <textarea
                              value={requiredSecretDrafts[secret.name] || ''}
                              onChange={(e) => setRequiredSecretDraft(secret.name, e.target.value)}
                              rows={4}
                              spellCheck={false}
                              placeholder={secret.is_set ? 'Paste replacement file contents' : 'Paste file contents'}
                              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          ) : (
                            <input
                              type="password"
                              value={requiredSecretDrafts[secret.name] || ''}
                              onChange={(e) => setRequiredSecretDraft(secret.name, e.target.value)}
                              autoComplete="off"
                              spellCheck={false}
                              placeholder={secret.is_set ? 'Enter replacement secret value' : 'Enter secret value'}
                              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          )}
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleSaveRequiredSecret(secret)}
                              disabled={secretSavingKey === secret.name}
                              className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                            >
                              {secretSavingKey === secret.name ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Key className="w-4 h-4" />
                              )}
                              {secret.is_set ? 'Update Value' : 'Save Value'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mb-4 rounded-lg border border-border/60 bg-muted/30">
                  <button
                    type="button"
                    onClick={() => setShowManualSecretEditor((current) => !current)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left"
                  >
                    <div>
                      <p className="text-sm font-medium">Add custom secret key</p>
                      <p className="text-xs text-muted-foreground">
                        Advanced fallback for secrets that are not already declared by the manifest.
                      </p>
                    </div>
                    {showManualSecretEditor ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                  {showManualSecretEditor && (
                    <div className="px-3 pb-3 space-y-2 border-t border-border/60">
                      <input
                        type="text"
                        value={secretNameInput}
                        onChange={(e) => setSecretNameInput(e.target.value)}
                        placeholder="Secret name (e.g. OPENAI_API_KEY)"
                        className="w-full px-3 py-2 mt-3 rounded-lg border border-border bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <input
                        type="password"
                        value={secretValueInput}
                        onChange={(e) => setSecretValueInput(e.target.value)}
                        placeholder="Secret value"
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <input
                        type="text"
                        value={secretDescriptionInput}
                        onChange={(e) => setSecretDescriptionInput(e.target.value)}
                        placeholder="Description (optional)"
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <button
                        onClick={handleSaveSecret}
                        disabled={secretSavingKey === '__manual__'}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                      >
                        {secretSavingKey === '__manual__' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Key className="w-4 h-4" />
                        )}
                        Save Secret
                      </button>
                    </div>
                  )}
                </div>

                <h3 className="text-sm font-medium mb-2">Configured secrets</h3>
                {secretsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading secrets...
                  </div>
                ) : appSecrets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No secrets configured for this app.</p>
                ) : (
                  <div className="space-y-2">
                    {appSecrets.map((secret) => (
                      <div key={secret.name} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border">
                        <div className="min-w-0">
                          <p className="font-mono text-xs text-foreground break-all">{secret.name}</p>
                          {secret.description && (
                            <p className="text-xs text-muted-foreground mt-1">{secret.description}</p>
                          )}
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Updated {formatRelativeTime(secret.updated_at || secret.created_at)}
                          </p>
                        </div>
                        
                          <button title={`Delete the ${secret.name} environment variable`}
                            onClick={() => handleDeleteSecret(secret.name)}
                            disabled={secretDeleting === secret.name}
                            className="p-1.5 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                          >
                          {secretDeleting === secret.name ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </button>
                        
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* App Usage Stats */}
          {appName && <AppUsageStats appName={appName} />}

          {/* Danger Zone - Only shown to owner */}
          {canDelete && (
            <div className="p-6 rounded-xl border border-destructive/30 bg-destructive/5">
              <h2 className="text-lg font-semibold mb-4 text-destructive">Danger Zone</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Deleting this app removes it from your active list, but you can restore it for 30 days.
              </p>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete App
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {/* Edit App Info Modal */}
      {showEditInfo && (
        <EditAppInfoModal
          app={app}
          onClose={() => setShowEditInfo(false)}
          onSaved={(updated) => {
            setShowEditInfo(false)
            if (updated.name !== app.name) {
              navigate(`/apps/${updated.name}`, { replace: true })
            } else {
              setApp(updated)
            }
          }}
        />
      )}

      {showShareModal && (
        <ShareModalComponent
          app={app}
          onClose={() => setShowShareModal(false)}
          onShared={loadApp}
        />
      )}

      {/* Clone Modal */}
      {showCloneModal && (
        <CloneAppModal
          app={app}
          onClose={() => setShowCloneModal(false)}
          onCloned={(response) => {
            setShowCloneModal(false)
            navigate(`/apps/${response.name}`, {
              state: { cloneNotice: getCloneNotice(response) },
            })
          }}
        />
      )}

      {/* Rebuild Confirmation */}
      {showRebuildConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-orange-500" />
              Rebuild App
            </h2>
            <p className="text-muted-foreground text-sm mb-4">
              This will rebuild your app from source and reinstall all dependencies.
              The app will be briefly unavailable during the build.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRebuildConfirm(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowRebuildConfirm(false); handleRebuild() }}
                disabled={!!actionLoading}
                className="flex-1 px-4 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                Rebuild
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish confirmation with visibility settings */}
      {showPublishConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
              <Rocket className="w-5 h-5 text-primary" />
              Publish app
            </h2>
            <p className="text-muted-foreground text-sm mb-4">
              This creates a live version of your app.
              Your current work area will keep running alongside it.
            </p>

            {(publishPreflight || publishPreflightError) && (
              <div className="mb-4 rounded-lg border border-border bg-secondary/40 p-3 text-sm">
                <div className="flex items-start gap-2">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="font-medium">
                      {publishPreflightError ? 'Safety check unavailable' : 'Publishing check'}
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      {publishPreflightError || publishPreflight?.summary}
                    </p>
                    {publishPreflight?.warnings.length ? (
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                        {publishPreflight.warnings.map((warning) => (
                          <li key={`${warning.code}-${warning.message}`}>{warning.message}</li>
                        ))}
                      </ul>
                    ) : null}
                    {publishSourceSummary && (
                      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <dt className="text-muted-foreground">Saved version</dt>
                          <dd className="font-medium">{publishSavedVersion || 'Not saved yet'}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Changed files</dt>
                          <dd className="font-medium">{publishChangedFileCount ?? 'Unknown'}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Unsaved changes</dt>
                          <dd className="font-medium">{publishHasUnsavedChanges ? 'Yes' : 'No'}</dd>
                        </div>
                      </dl>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setShowPublishConfirm(false)
                        setFocusWorkRequest((value) => value + 1)
                      }}
                      className="mt-3 text-sm font-medium text-primary hover:underline"
                    >
                      View work details
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Visibility dropdown */}
            <div className="mb-4">
              <label className="text-sm font-medium mb-1.5 block">Who can access this app?</label>
              <StyledSelect
                value={publishAccessLevel}
                onChange={setPublishAccessLevel}
                options={[
                  { value: 'personal', label: 'Private', description: 'Only you can access', icon: Lock },
                  { value: 'specific', label: 'Specific people', description: 'Only people you add', icon: UserCheck },
                  { value: 'link', label: 'Anyone with the link', description: 'Any platform user with the link', icon: Link2 },
                  { value: 'platform', label: 'Platform', description: 'Visible to all platform users in Browse Tab', icon: Users },
                ]}
                className="w-full"
              />
            </div>

            {/* Browse visibility info */}
            {publishAccessLevel === 'platform' && (
              <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-blue-600 dark:text-blue-400 flex items-start gap-2">
                <Globe className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Your app will be discoverable by all users in the Browse tab.</span>
              </div>
            )}
            {publishAccessLevel === 'link' && (
              <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-blue-600 dark:text-blue-400 flex items-start gap-2">
                <Link2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Visible to any platform user with the link (not visible in Browse tab).</span>
              </div>
            )}

            {/* Specific users hint */}
            {publishAccessLevel === 'specific' && (
              <div className="mb-4 p-3 rounded-lg bg-secondary text-xs text-muted-foreground flex items-start gap-2">
                <UserCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  You can add specific users after publishing from the <strong>Sharing</strong> section on this page.
                </span>
              </div>
            )}

            <p className="text-muted-foreground text-xs mb-4">
              This may take a few minutes. You can continue working while it builds.
              You can change visibility settings later from the app details page.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPublishConfirm(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowPublishConfirm(false); handleProductionBuild(publishAccessLevel) }}
                disabled={!!actionLoading}
                className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                Publish app
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">Delete "{app.name}"?</h2>
            <p className="text-muted-foreground mb-4">
              This will remove the app from active use and stop its containers. You can restore it for 30 days.
            </p>
            {app.is_template && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                This is an active template. Deleting it will remove it from the gallery.
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={!!actionLoading}
                className="flex-1 px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
              >
                {actionLoading === 'delete' ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
